// components/CoursePlanner/BookmarkedCourses.tsx
import { useState, useRef } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { Plus, BookOpen, ChevronRight, ChevronDown, MoreVertical } from 'lucide-react';
import CourseCard from '../shared/CourseCard';
import { CourseListSkeleton } from '../shared/CourseCardSkeleton';
import { useConfirm } from '../../contexts/ConfirmContext';
import { DraggableCourse, SemesterDropZone, useDragDrop } from '../../contexts/DragDropContext';
import { usePlanOperations } from '../../contexts/PlanOperationsContext';
import { AddBookmarkModal } from '../shared/modals';
import type { EnrolledCourse, AcademicPlan } from '../../types';
import { useCreditUnit } from '../../contexts/InstitutionConfigContext';

const calculateTotalCredits = (courses: EnrolledCourse[]): number => {
  return courses.reduce((sum, course) => sum + (course?.credits || 0), 0);
};

interface BookmarksHeaderMenuProps {
  onOpenAddModal: () => void;
  onClearBookmarks: () => void;
  courseCount: number;
}

const BookmarksHeaderMenu = ({
  onOpenAddModal,
  onClearBookmarks,
  courseCount,
}: BookmarksHeaderMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { confirm } = useConfirm();

  useClickOutside(menuRef, () => setIsOpen(false), isOpen);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors"
        aria-label="Bookmarks options"
      >
        <MoreVertical className="h-4 w-4 text-gray-600" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <button
            onClick={() => handleAction(onOpenAddModal)}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
          >
            Add Course
          </button>

          {courseCount > 0 && (
            <>
              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={() => handleAction(async () => {
                  const confirmed = await confirm({
                    title: 'Clear Bookmarks',
                    message: `Clear all ${courseCount} bookmarked courses?`,
                    confirmText: 'Clear All',
                    variant: 'danger'
                  });
                  if (confirmed) {
                    onClearBookmarks();
                  }
                })}
                className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors text-red-600"
              >
                Clear Bookmarked Courses
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface EmptyBookmarksStateProps {
  isDropTarget: boolean;
  onOpenAddModal: () => void;
}

const EmptyBookmarksState = ({ isDropTarget, onOpenAddModal }: EmptyBookmarksStateProps) => {
  if (isDropTarget) {
    return (
      <div className="text-center py-6 transition-all duration-200 text-primary">
        <BookOpen className="h-8 w-8 mx-auto mb-2 animate-bounce" />
        <p className="text-sm font-medium">Drop here</p>
      </div>
    );
  }

  return (
    <div className="text-center py-4 px-4">
      <button
        onClick={onOpenAddModal}
        className="text-xs px-3 py-1.5 text-gray-500 hover:text-stone-600 border border-dashed border-gray-300 hover:border-stone-400 rounded-md transition-colors flex items-center justify-center gap-1.5 mx-auto"
      >
        <Plus className="h-3.5 w-3.5" />
        Add course or drag here
      </button>
    </div>
  );
};

interface BookmarkedCoursesProps {
  loading: boolean;
  bookmarkedCourses: EnrolledCourse[];
  bookmarkedCollapsed: boolean;
  onToggleBookmarks: () => void;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  dropdownContext?: string;
  academicPlan: AcademicPlan;
}

const BookmarkedCourses = ({
  loading,
  bookmarkedCourses,
  bookmarkedCollapsed,
  onToggleBookmarks,
  openDropdownId,
  setOpenDropdownId,
  dropdownContext,
  academicPlan,
}: BookmarkedCoursesProps) => {
  const creditUnit = useCreditUnit();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const {
    onRemoveFromBookmarks,
    onMoveToSemester,
    onAddToBookmarks,
    onEditPlaceholder,
    onConvertPlaceholder,
  } = usePlanOperations();

  const { dragOverSemester } = useDragDrop();

  const isDropTarget = dragOverSemester === 'bookmarks';
  const isEmpty = bookmarkedCourses.length === 0;
  const totalCredits = calculateTotalCredits(bookmarkedCourses);

  const handleClearBookmarks = () => {
    for (let i = bookmarkedCourses.length - 1; i >= 0; i--) {
      onRemoveFromBookmarks(i);
    }
  };

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  if (loading) {
    return (
      <div className="animate-fade-in print-semester">
        <div className="border rounded-lg transition-all duration-200 border-gray-200">
          <div className="bg-stone-200 hover:bg-stone-300 border-stone-300 border-b rounded-t-lg p-3 flex items-center justify-between transition-all duration-200 print-semester-header">
            <div className="flex-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">
                  Bookmarked Courses
                </h3>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
                  <span>•</span>
                  <div className="animate-pulse bg-gray-200 h-4 w-16 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4">
            <CourseListSkeleton variant="compact" count={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in print-semester">
      <div
        className={`border rounded-lg transition-all duration-200 ${
          isDropTarget && isEmpty
            ? 'border-primary bg-primary-50 shadow-lg scale-[1.02]'
            : isDropTarget
            ? 'border-stone-400 bg-stone-50 shadow-lg'
            : 'border-gray-200'
        }`}
      >
        <div className="bg-stone-200 hover:bg-stone-300 border-stone-300 border-b rounded-t-lg p-3 flex items-start justify-between transition-all duration-200 print-semester-header">
          <div
            className="flex-1 cursor-pointer"
            onClick={() => onToggleBookmarks()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">
                  Bookmarked Courses
                </h3>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAddModal();
                  }}
                  className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors print-hide"
                  title="Add course"
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleBookmarks();
                  }}
                >
                  {bookmarkedCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-gray-600 transition-transform duration-200 print-hide" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200 print-hide" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
              <span>{bookmarkedCourses.length} courses</span>
              <span>·</span>
              <span>{creditUnit.format(totalCredits)}</span>
            </div>
          </div>
          <div
            className="ml-2 print-hide"
            onClick={(e) => e.stopPropagation()}
          >
            <BookmarksHeaderMenu
              onOpenAddModal={handleOpenAddModal}
              onClearBookmarks={handleClearBookmarks}
              courseCount={bookmarkedCourses.length}
            />
          </div>
        </div>

        <SemesterDropZone
          zone="bookmarks"
          data-testid="bookmarks-drop-zone"
          className={`transition-all duration-200 ${
            isEmpty
              ? isDropTarget
                ? 'bg-primary-50'
                : 'bg-gray-50'
              : 'p-4'
          } ${bookmarkedCollapsed ? 'hidden print:block' : ''} print-semester-courses`}
        >
          {isEmpty ? (
            <div className="print-hide">
              <EmptyBookmarksState
                isDropTarget={isDropTarget}
                onOpenAddModal={handleOpenAddModal}
              />
            </div>
          ) : (
            <div className="space-y-2 min-h-[80px] print:min-h-0">
              {isDropTarget && (
                <div className="text-xs text-primary text-center py-2 font-medium animate-pulse print-hide">
                  Drop to add course to bookmarks
                </div>
              )}

              {bookmarkedCourses.map((course, index) => (
                <div
                  key={`bookmarks-${course.code || (course as { id?: string | number }).id || index}`}
                  className="animate-fade-in print-course-item"
                >
                  <span className="print:block hidden">
                    <span className="font-medium">{course.code} - {course.title}</span>
                  </span>
                  <span className="print:block hidden text-sm">
                    {course.credits} credits
                  </span>

                  <div className="print:hidden">
                    <DraggableCourse
                      id={`bookmarks::${index}`}
                      course={course}
                      from="bookmarks"
                      index={index}
                    >
                    {(dragBind) => (
                    <CourseCard
                      course={course}
                      variant="ultra-compact"
                      semester="bookmarks"

                      showCredits={true}
                      showDescription={false}
                      showBadges={['type']}
                      showBadgeIcons={true}

                      actions={course.isPlaceholder ? [] : [
                        { type: 'move' },
                        { type: 'remove' }
                      ]}

                      dragBind={dragBind}
                      onAction={(actionType: string) => {
                        if (course.isPlaceholder) {
                          if (actionType === 'edit') {
                            onEditPlaceholder(course);
                          } else if (actionType === 'convert') {
                            onConvertPlaceholder(course, 'bookmarks', index);
                          } else if (actionType === 'remove') {
                            onRemoveFromBookmarks(index);
                          }
                        } else {
                          if (actionType === 'move') {
                            onMoveToSemester(course, 'bookmarks', index);
                          } else if (actionType === 'remove') {
                            onRemoveFromBookmarks(index);
                          }
                        }
                      }}

                      isDragging={dragBind.isDragging}

                      dropdownContext={dropdownContext}
                      contextId={`bookmarks-${course.code || course}`}
                      index={index}

                      openDropdownId={openDropdownId}
                      setOpenDropdownId={setOpenDropdownId}
                    />
                    )}
                    </DraggableCourse>
                  </div>
                </div>
              ))}

            </div>
          )}
        </SemesterDropZone>
      </div>

      <AddBookmarkModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddToBookmarks={onAddToBookmarks}
        academicPlan={academicPlan}
      />
    </div>
  );
};

export default BookmarkedCourses;
